/**
 * Compass Explorer — focus filter
 *
 * Pure function that slices the full folder graph down to what should
 * render for a given {focus, zoomLevel}. Drives the client-side layout
 * pipeline: `CompassExplorerCanvas` calls `sliceForFocus(graph, focus,
 * zoom)`, feeds the result into `InfraLayoutEngine`, and renders the
 * laid-out shapes.
 *
 * Design principles:
 *  - Pure. No side effects, no store access. Easy to unit test.
 *  - Side-effect-free on the input graph. Never mutates.
 *  - Zoom drives *what kind* of nodes we emit, not just filtering —
 *    at L1/L2/L3 we emit synthetic account/VPC/subnet bubbles, at
 *    L4 we emit derived workload bubbles, at unfiltered L5 we emit
 *    resource-type overview bubbles, and focused L5 emits real resources.
 *  - Radius-aware for `resource` focus: include N-hops of neighbours.
 */

import type { PersistedGraph, PersistedNode, PersistedEdge } from '../../models/KnowledgeGraph';
import type { FocusSpec, ZoomLevel } from '../../../../stores/compassViewPrefsStore';

export interface SlicedSubgraph {
  /** Leaf resource nodes that the layout engine should place. */
  leafNodes: PersistedNode[];
  /** Container nodes (vpc/subnet/region/account). */
  containerNodes: PersistedNode[];
  /** Synthetic bubble nodes for L1/L2 overview rendering. */
  bubbleNodes: BubbleNode[];
  /** Containment edges inside the slice. */
  containmentEdges: PersistedEdge[];
  /** Non-containment flow edges inside the slice. */
  flowEdges: PersistedEdge[];
  /** Debug label for logs/telemetry. */
  sliceLabel: string;
}

export interface BubbleNode {
  id: string;
  type: 'compass_bubble';
  label: string;
  kind: 'account' | 'region' | 'vpc' | 'subnet' | 'workload' | 'resource_type';
  referenceId: string;      // id of the container/resource this bubble stands for
  /** Raw graph resources represented by this synthetic bubble. */
  memberIds?: readonly string[];
  /** Workload/resource grouping strategy used to derive this bubble. */
  groupKind?: 'asg' | 'stack' | 'service' | 'module' | 'resource_type';
  resourceCount: number;
  tierBreakdown: Record<string, number>;
  /**
   * Sum of descendant-resource `cost.monthly` in USD, or `undefined`
   * when no descendant carries cost data. Populated by
   * `collapseForOverview` at slice time so renderers can show a cost
   * badge without re-walking the graph. Partial-coverage scans keep
   * the number they've got — it's a lower bound, not an assertion.
   */
  monthlyCostUsd?: number;
  /**
   * Fraction of descendant resources that carry a cost attribution.
   * `1.0` → full coverage, `0.0` → no cost data anywhere. The header
   * uses this to pick between "$1,234/mo" and "$1,234+/mo (45%)".
   */
  costCoverage?: number;
  /** Average utilization percentage across descendants that report it. */
  utilizationPct?: number;
  /** Estimated monthly spend on idle/under-utilized capacity. */
  wastedMonthlyUsd?: number;
  /**
   * Top severity across descendant resources' findings + count.
   * Populated by the scene builder's severity roll-up pass so a
   * VPC / workload / resource-type bubble can paint a top-left
   * severity dot ("critical · 4" etc.). Cost anomalies are
   * deliberately excluded — they roll up via `monthlyCostUsd`
   * instead.
   */
  findingSeverity?: 'critical' | 'high' | 'medium' | 'low' | 'info';
  findingCount?: number;
  /**
   * Set by `buildExplorerScene` when quick-filter chips are active
   * and *none* of this bubble's members pass the union of chip
   * predicates. The bubble scene builders consume this to stamp
   * `attrs.dimmed=true` so the canvas signals "nothing under this
   * card matches your filter" without removing it entirely.
   */
  filterDimmed?: boolean;
  /**
   * When this bubble is in the caller's expanded set, the interior
   * resources are flattened into a dot list so the card can render a
   * preview grid in place. Absent otherwise — keeps the compact path
   * cheap.
   */
  interior?: BubbleInteriorDot[];
  /**
   * Spatial context derived from member ancestors. Populated by
   * `computeBubbleScope` so the card can show a breadcrumb without
   * re-walking the parent chain.
   */
  scope?: BubbleScope;
}

export interface BubbleInteriorDot {
  /** Graph node id — doubles as the drill target. */
  id: string;
  /** Short resource type (`ec2`, `rds`, `alb`, `iam_role`). */
  resourceType: string;
  /** Display name / tag for hover tooltips. Falls back to id. */
  label: string;
}

/**
 * Spatial address of a bubble — answers "where in the estate is this?"
 * Stamped at slice time by walking the containment parent chain from
 * `memberIds`. The card renderer surfaces it as a thin breadcrumb
 * under the title so a card like "5 × ALB" never floats without
 * context.
 *
 * When members span multiple VPCs or subnets, the corresponding
 * field is omitted and `multi: true` / `spanCount` is set instead so
 * the renderer can show "Multiple VPCs (3)".
 */
export interface BubbleScope {
  account?: { id: string; label: string };
  region?: { id: string; label: string };
  vpc?: { id: string; label: string };
  subnet?: { id: string; label: string };
  /** True when members are spread across more than one container at any level. */
  multi?: boolean;
  /** Distinct VPC count when members span multiple VPCs. */
  vpcSpan?: number;
  /** Distinct subnet count when members span multiple subnets. */
  subnetSpan?: number;
}

const CONTAINER_SHORT_TYPES = new Set(['vpc', 'subnet', 'region', 'account']);
const GENERIC_MANIFEST_IDS = new Set(['aws_generic', 'generic', 'resource']);

/**
 * Edge types we treat as "parent → child" containment. Importers emit
 * different shapes depending on their vintage — ontology importers use
 * `contains` / `containment`, AWS scan converters use `in-vpc` /
 * `in-subnet`, the CFN importer emits `parent-of`. They all mean the
 * same thing for layout. Sibling code (`SearchIndexService`,
 * `CompassFolderResourcesSection`) already uses the broadened set; this
 * module was the outlier and it's why the L3+ layout was showing a flat
 * shared-lane fallback on real scan data (no VPC frames because
 * `InfraLayoutEngine` saw zero containment edges and therefore
 * identified zero VPCs).
 */
const CONTAINMENT_EDGE_TYPES = new Set([
  'contains',
  'containment',
  'in-vpc',
  'in-subnet',
  'parent-of',
]);

function isContainmentEdgeType(edgeType: string | undefined): boolean {
  return !!edgeType && CONTAINMENT_EDGE_TYPES.has(edgeType);
}

function normalizeShortResourceType(value: string): string {
  const trimmed = value.trim();
  const withoutAwsPrefix = trimmed.startsWith('aws_') ? trimmed.slice(4) : trimmed;
  return withoutAwsPrefix.replace(/-/g, '_').toLowerCase();
}

/**
 * Extract the short resource type from a graph node. Tries three
 * locations in priority order. Generic manifests defer to
 * `props.resourceType` so universal AWS nodes still group and render
 * as `iam_role`, `sg`, `s3`, etc.
 */
function resourceTypeOf(node: PersistedNode): string {
  const props = (node.props ?? {}) as Record<string, unknown>;
  const mid = (props._manifestId as string | undefined) ?? (props.manifestId as string | undefined);
  const propResourceType = typeof props.resourceType === 'string' ? props.resourceType : undefined;
  if (propResourceType && (!mid || GENERIC_MANIFEST_IDS.has(mid))) {
    return normalizeShortResourceType(propResourceType);
  }
  if (mid) return normalizeShortResourceType(mid);
  if (node.type && node.type.startsWith('aws_')) return normalizeShortResourceType(node.type);
  return normalizeShortResourceType(node.type);
}

function numberProp(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function nestedProp(root: Record<string, unknown>, path: readonly string[]): unknown {
  let cur: unknown = root;
  for (const key of path) {
    if (!cur || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[key];
  }
  return cur;
}

function publicExposureLevel(node: PersistedNode): 'none' | 'restricted' | 'open' | undefined {
  const props = (node.props ?? {}) as Record<string, unknown>;
  const value = props.publicExposure ?? nestedProp(props, ['access', 'publicExposure']);
  if (value === true) return 'open';
  if (value === false) return 'none';
  if (typeof value !== 'string') return undefined;
  const normalized = value.toLowerCase();
  if (normalized === 'open' || normalized === 'public' || normalized === 'internet') return 'open';
  if (normalized === 'restricted') return 'restricted';
  if (normalized === 'none' || normalized === 'private') return 'none';
  return undefined;
}

function isContainerNode(node: PersistedNode): boolean {
  return CONTAINER_SHORT_TYPES.has(resourceTypeOf(node));
}

function isRuntimeResource(node: PersistedNode): boolean {
  const rt = resourceTypeOf(node);
  return (
    rt === 'ec2' ||
    rt === 'ecs' ||
    rt === 'ecs_service' ||
    rt === 'lambda' ||
    rt === 'asg' ||
    rt === 'eks' ||
    rt === 'rds' ||
    rt === 'rds_instance' ||
    rt === 'rds_cluster' ||
    rt === 'dynamodb' ||
    rt === 'alb' ||
    rt === 'nlb' ||
    rt === 'elb' ||
    rt === 'api_gateway'
  );
}

/**
 * Slice the graph down to what the given focus + zoom should render.
 *
 * @param graph  Full folder graph (in-memory).
 * @param focus  What the user is centered on.
 * @param zoom   Level-of-detail band.
 */
export function sliceForFocus(
  graph: PersistedGraph,
  focus: FocusSpec,
  zoom: ZoomLevel,
): SlicedSubgraph {
  // Build indexes once — these are cheap (a few hundred entries even
  // for 10k-resource accounts).
  const byId = new Map<string, PersistedNode>();
  for (const n of graph.nodes) byId.set(n.id, n);

  const containmentParentOf = new Map<string, string>();   // child → parent
  const childrenOf = new Map<string, string[]>();          // parent → children
  for (const e of graph.edges) {
    if (!isContainmentEdgeType(e.type)) continue;
    // First-parent-wins: matches InfraLayoutEngine's rule.
    if (!containmentParentOf.has(e.to)) containmentParentOf.set(e.to, e.from);
    const arr = childrenOf.get(e.from) ?? [];
    arr.push(e.to);
    childrenOf.set(e.from, arr);
  }

  // ── Prop-based parentage fallback ─────────────────────────────
  // Older ingests didn't always emit containment edges — many scans
  // only carry the relationship as `props.subnetId` / `props.vpcId` /
  // `props.accountId` on each resource. Without a parent link the
  // layout engine sees zero VPCs and falls back to a flat shared-lane
  // layout (the "no frames + horizontal row" regression). Reconstruct
  // the parent chain from props when edges don't cover a node.
  //
  // Priority matches `parseScanBundle` (tightest wins):
  //   subnet > vpc > cluster > account
  //
  // Only used when an ID resolves to a real in-graph container node,
  // so we don't invent phantom parents.
  const containerIdByShortType = new Map<string, Set<string>>();
  for (const n of graph.nodes) {
    if (!isContainerNode(n)) continue;
    const rt = resourceTypeOf(n);
    if (!containerIdByShortType.has(rt)) containerIdByShortType.set(rt, new Set());
    containerIdByShortType.get(rt)!.add(n.id);
  }
  const knownContainer = (id: string): boolean => {
    if (!id) return false;
    return byId.has(id) && isContainerNode(byId.get(id)!);
  };
  const addParentLink = (childId: string, parentId: string | undefined): boolean => {
    if (!parentId) return false;
    if (parentId === childId) return false;
    if (!knownContainer(parentId)) return false;
    if (containmentParentOf.has(childId)) return false;
    containmentParentOf.set(childId, parentId);
    const arr = childrenOf.get(parentId) ?? [];
    arr.push(childId);
    childrenOf.set(parentId, arr);
    return true;
  };
  for (const n of graph.nodes) {
    if (containmentParentOf.has(n.id)) continue;
    const props = (n.props ?? {}) as Record<string, unknown>;
    const subnetId = typeof props.subnetId === 'string' ? props.subnetId : undefined;
    const vpcId = typeof props.vpcId === 'string' ? props.vpcId : undefined;
    const accountId = typeof props.accountId === 'string' ? props.accountId : undefined;
    if (addParentLink(n.id, subnetId)) continue;
    if (addParentLink(n.id, vpcId)) continue;
    if (addParentLink(n.id, accountId)) continue;
  }

  // Development-time diagnostic: when a graph has no containment
  // structure, the Explorer layout falls back to a flat tier layout
  // and users report "no VPC frames". Surfacing the reason in the
  // console makes the problem identifiable without a server round-
  // trip. Controlled by a window-level flag so production doesn't
  // spam unless explicitly enabled.
  if (
    typeof window !== 'undefined' &&
    (window as unknown as { __COMPASS_DEBUG__?: boolean }).__COMPASS_DEBUG__
  ) {
    const edgeTypeCounts: Record<string, number> = {};
    for (const e of graph.edges) edgeTypeCounts[e.type] = (edgeTypeCounts[e.type] ?? 0) + 1;
    const containerCountsByType: Record<string, number> = {};
    for (const n of graph.nodes) {
      if (isContainerNode(n)) {
        const rt = resourceTypeOf(n);
        containerCountsByType[rt] = (containerCountsByType[rt] ?? 0) + 1;
      }
    }
    const sampleNodeWithProps = graph.nodes.find((n) => {
      const p = (n.props ?? {}) as Record<string, unknown>;
      return p.vpcId || p.subnetId || p.accountId;
    });
    const parentCoveragePercent =
      graph.nodes.length > 0
        ? Math.round((containmentParentOf.size / graph.nodes.length) * 100)
        : 0;
    // eslint-disable-next-line no-console
    console.info('[compass:focusFilter] graph diagnostic', {
      totalNodes: graph.nodes.length,
      totalEdges: graph.edges.length,
      edgeTypeCounts,
      containerCountsByType,
      parentLinksBuilt: containmentParentOf.size,
      parentCoveragePercent,
      sampleNodeWithPropsId: sampleNodeWithProps?.id,
      sampleNodeProps: sampleNodeWithProps
        ? Object.keys((sampleNodeWithProps.props ?? {}) as Record<string, unknown>).slice(0, 20)
        : null,
    });
  }

  const findAncestor = (id: string, kind: string, max = 16): string | null => {
    let cur = containmentParentOf.get(id);
    let hops = 0;
    while (cur && hops++ < max) {
      const c = byId.get(cur);
      if (c && resourceTypeOf(c) === kind) return cur;
      cur = containmentParentOf.get(cur);
    }
    return null;
  };

  // Collect the "in-slice" id set for the focus.
  const inSlice = resolveFocusIds(focus, graph, byId, childrenOf, findAncestor);

  // Ancestor preservation — every resource in the slice keeps its
  // parent container chain (subnet → vpc → account → region). Without
  // this, preset views (attack-surface, cost-waste, ownership-gaps,
  // etc.) drop the frames the resources live in, leaving floating
  // cards with no spatial context. The frames are cheap to carry; the
  // layout engine ignores them when they have no leaves left after
  // the L4/L5 collapses.
  if (focus.kind !== 'overview') {
    const additions: string[] = [];
    for (const id of inSlice) {
      for (const kind of ['subnet', 'vpc', 'account', 'region'] as const) {
        const ancestor = findAncestor(id, kind);
        if (ancestor && !inSlice.has(ancestor)) additions.push(ancestor);
      }
    }
    for (const id of additions) inSlice.add(id);
  }

  // Partition into leaves + containers + surface bubbles.
  const leafNodes: PersistedNode[] = [];
  const containerNodes: PersistedNode[] = [];
  for (const id of inSlice) {
    const n = byId.get(id);
    if (!n) continue;
    if (isContainerNode(n)) containerNodes.push(n);
    else leafNodes.push(n);
  }

  // Keep only edges where both endpoints are in the slice (structural
  // edges excluded later in the materializer-style step).
  const containmentEdges: PersistedEdge[] = [];
  const flowEdges: PersistedEdge[] = [];
  const seenContainmentPair = new Set<string>();
  for (const e of graph.edges) {
    if (!inSlice.has(e.from) || !inSlice.has(e.to)) continue;
    if (isContainmentEdgeType(e.type)) {
      containmentEdges.push(e);
      seenContainmentPair.add(`${e.from}→${e.to}`);
    } else flowEdges.push(e);
  }

  // Synthesise containment edges for prop-based parentage that wasn't
  // expressed as a graph edge in ingestion. `InfraLayoutEngine` rebuilds
  // its own `parentOf` map from `containmentEdges` alone — without these
  // synthetic edges the engine would still see a flat graph even after
  // the prop-based fallback populated the slicer's own parent map.
  //
  // The provenance block is a stable, minimal stub: we don't know the
  // original ingest jobs for a synthetic edge, so we label it clearly.
  const SYNTHETIC_PROVENANCE = {
    sourceJobIds: [] as string[],
    firstSeen: '',
  };
  for (const [childId, parentId] of containmentParentOf) {
    if (!inSlice.has(childId) || !inSlice.has(parentId)) continue;
    const key = `${parentId}→${childId}`;
    if (seenContainmentPair.has(key)) continue;
    seenContainmentPair.add(key);
    containmentEdges.push({
      id: `synthetic-containment-${parentId}-${childId}`,
      type: 'contains',
      from: parentId,
      to: childId,
      props: { synthetic: true },
      provenance: SYNTHETIC_PROVENANCE,
    } as PersistedEdge);
  }

  // Zoom-level transformations. At L1/L2/L3 we replace leaves under
  // account/VPC/subnet containers with synthetic bubbles. At L4 we
  // derive workload/stack bubbles from scan tags. At unfiltered L5 we
  // keep the overview readable by grouping atomic resources by type;
  // focused L5 still emits real resources.
  let bubbleNodes: BubbleNode[] = [];
  let effectiveLeaves = leafNodes;
  let effectiveContainers = containerNodes;
  if (zoom === 4) {
    bubbleNodes = collapseForWorkloads(effectiveLeaves, findAncestor, byId, containmentParentOf);
    if (bubbleNodes.length > 0) {
      effectiveContainers = [];
      effectiveLeaves = [];
    }
  } else if (zoom === 5 && focus.kind === 'overview') {
    bubbleNodes = collapseForResourceTypes(effectiveLeaves, byId, containmentParentOf);
    if (bubbleNodes.length > 0) {
      effectiveContainers = [];
      effectiveLeaves = [];
    }
  } else if (zoom <= 3 && isStructuralFocus(focus)) {
    const { bubbles, survivingContainers } = collapseForOverview(
      zoom,
      effectiveContainers,
      leafNodes,
      containmentParentOf,
      childrenOf,
      byId,
    );
    bubbleNodes = bubbles;
    effectiveContainers = survivingContainers;
    effectiveLeaves = [];
  }

  return {
    leafNodes: effectiveLeaves,
    containerNodes: effectiveContainers,
    bubbleNodes,
    containmentEdges,
    flowEdges,
    sliceLabel: describeFocus(focus, zoom),
  };
}

function isStructuralFocus(focus: FocusSpec): boolean {
  return focus.kind === 'overview' || focus.kind === 'account' || focus.kind === 'vpc';
}

/**
 * Walk the parent chain from every member to build the bubble's
 * spatial address. If members live in a single account/region/vpc/
 * subnet, that level is reported; if they span multiple, the level is
 * omitted and `multi: true` plus a span count is set.
 */
function computeBubbleScope(
  memberIds: readonly string[],
  parentOf: Map<string, string>,
  byId: Map<string, PersistedNode>,
): BubbleScope | undefined {
  if (memberIds.length === 0) return undefined;
  const findKind = (startId: string, kind: string, max = 16): PersistedNode | null => {
    let cur = parentOf.get(startId);
    let hops = 0;
    while (cur && hops++ < max) {
      const n = byId.get(cur);
      if (n && resourceTypeOf(n) === kind) return n;
      cur = parentOf.get(cur);
    }
    return null;
  };
  const accountIds = new Set<string>();
  const regionIds = new Set<string>();
  const vpcIds = new Set<string>();
  const subnetIds = new Set<string>();
  const labelOf = new Map<string, string>();
  for (const id of memberIds) {
    for (const [bucket, kind] of [
      [accountIds, 'account'],
      [regionIds, 'region'],
      [vpcIds, 'vpc'],
      [subnetIds, 'subnet'],
    ] as const) {
      const ancestor = findKind(id, kind);
      if (ancestor) {
        bucket.add(ancestor.id);
        if (!labelOf.has(ancestor.id)) {
          labelOf.set(ancestor.id, nodeDisplayLabel(ancestor) ?? ancestor.id);
        }
      }
    }
  }
  const pickSingle = (ids: Set<string>): { id: string; label: string } | undefined => {
    if (ids.size !== 1) return undefined;
    const id = [...ids][0];
    return { id, label: labelOf.get(id) ?? id };
  };
  const scope: BubbleScope = {};
  const account = pickSingle(accountIds);
  if (account) scope.account = account;
  const region = pickSingle(regionIds);
  if (region) scope.region = region;
  const vpc = pickSingle(vpcIds);
  if (vpc) scope.vpc = vpc;
  const subnet = pickSingle(subnetIds);
  if (subnet) scope.subnet = subnet;
  if (vpcIds.size > 1) {
    scope.multi = true;
    scope.vpcSpan = vpcIds.size;
  }
  if (subnetIds.size > 1) {
    scope.multi = true;
    scope.subnetSpan = subnetIds.size;
  }
  // Empty scope (no ancestors found anywhere) — return undefined so
  // the renderer can decide to hide the breadcrumb row entirely.
  if (
    !scope.account && !scope.region && !scope.vpc && !scope.subnet &&
    !scope.multi
  ) {
    return undefined;
  }
  return scope;
}

function collapseForWorkloads(
  leaves: PersistedNode[],
  findAncestor: (id: string, kind: string) => string | null,
  byId: Map<string, PersistedNode>,
  parentOf: Map<string, string>,
): BubbleNode[] {
  const groups = new Map<string, { label: string; groupKind: NonNullable<BubbleNode['groupKind']>; members: PersistedNode[] }>();

  for (const node of leaves) {
    if (isContainerNode(node)) continue;
    const tags = awsTagsOf(node);
    const scopeId = findAncestor(node.id, 'vpc') ?? findAncestor(node.id, 'account') ?? 'global';
    const scopeLabel = scopeId === 'global' ? 'Global' : nodeDisplayLabel(byId.get(scopeId)) ?? scopeId;
    const grouping = workloadGroupingFor(node, tags, scopeLabel);
    const key = `${grouping.groupKind}:${scopeId}:${grouping.rawValue}`;
    const existing = groups.get(key);
    if (existing) {
      existing.members.push(node);
    } else {
      groups.set(key, { label: grouping.label, groupKind: grouping.groupKind, members: [node] });
    }
  }

  return Array.from(groups.values())
    .map((group) => buildResourceBubble({
      kind: 'workload',
      groupKind: group.groupKind,
      label: group.label,
      members: group.members,
      parentOf,
      byId,
    }))
    .sort(compareBubbles);
}

function collapseForResourceTypes(
  leaves: PersistedNode[],
  byId: Map<string, PersistedNode>,
  parentOf: Map<string, string>,
): BubbleNode[] {
  const groups = new Map<string, PersistedNode[]>();
  for (const node of leaves) {
    if (isContainerNode(node)) continue;
    const rt = resourceTypeOf(node);
    const list = groups.get(rt) ?? [];
    list.push(node);
    groups.set(rt, list);
  }

  return Array.from(groups.entries())
    .map(([resourceType, members]) => buildResourceBubble({
      kind: 'resource_type',
      groupKind: 'resource_type',
      label: resourceTypeLabel(resourceType),
      members,
      parentOf,
      byId,
    }))
    .sort(compareBubbles);
}

function workloadGroupingFor(
  node: PersistedNode,
  tags: Record<string, string>,
  scopeLabel: string,
): { groupKind: NonNullable<BubbleNode['groupKind']>; rawValue: string; label: string } {
  const asg = tagValue(tags, ['aws:autoscaling:groupName', 'autoscaling:groupName', 'AutoScalingGroupName']);
  if (asg) return { groupKind: 'asg', rawValue: asg, label: scopedLabel(asg, scopeLabel) };

  const stack = tagValue(tags, ['Stack', 'stack', 'aws:cloudformation:stack-name', 'cloudformation:stack-name']);
  if (stack) return { groupKind: 'stack', rawValue: stack, label: scopedLabel(stack, scopeLabel) };

  const service = tagValue(tags, ['Service', 'service', 'Application', 'application', 'App', 'app']);
  if (service) return { groupKind: 'service', rawValue: service, label: scopedLabel(service, scopeLabel) };

  const module = tagValue(tags, ['Module', 'module', 'Component', 'component']);
  if (module) return { groupKind: 'module', rawValue: module, label: scopedLabel(module, scopeLabel) };

  const resourceType = resourceTypeOf(node);
  return {
    groupKind: 'resource_type',
    rawValue: resourceType,
    label: scopedLabel(resourceTypeLabel(resourceType), scopeLabel),
  };
}

function buildResourceBubble(args: {
  kind: Extract<BubbleNode['kind'], 'workload' | 'resource_type'>;
  groupKind: NonNullable<BubbleNode['groupKind']>;
  label: string;
  members: PersistedNode[];
  parentOf: Map<string, string>;
  byId: Map<string, PersistedNode>;
}): BubbleNode {
  // Defensive: some ingest sources occasionally emit graph nodes
  // without a top-level `id` (the AWS hosted scanner's `place` rows
  // can land in that shape when the merge pass hasn't filled the
  // primary id yet). Filtering them out keeps the L5-overview sort
  // from throwing on `undefined.localeCompare(...)` and erasing the
  // whole scene.
  const members = [...args.members]
    .filter((n) => typeof n.id === 'string' && n.id.length > 0)
    .sort((a, b) => a.id.localeCompare(b.id));
  const representative = members[0];
  const memberIds = members.map((m) => m.id);
  let costCarriers = 0;
  let monthlyCostUsd = 0;
  let utilizationCarriers = 0;
  let utilizationPctTotal = 0;
  let wastedMonthlyUsd = 0;
  const tierBreakdown: Record<string, number> = {};
  const interior: BubbleInteriorDot[] = [];

  for (const node of members) {
    const rt = resourceTypeOf(node);
    tierBreakdown[rt] = (tierBreakdown[rt] ?? 0) + 1;
    interior.push({
      id: node.id,
      resourceType: rt,
      label: nodeDisplayLabel(node) ?? node.id,
    });
    const monthly = readMonthlyCostUsd(node);
    if (monthly !== null) {
      costCarriers++;
      monthlyCostUsd += monthly;
    }
    const utilization = readUtilizationPct(node);
    if (utilization !== null) {
      utilizationCarriers++;
      utilizationPctTotal += utilization;
      if (monthly !== null) {
        wastedMonthlyUsd += monthly * (1 - Math.max(0, Math.min(100, utilization)) / 100);
      }
    }
  }

  return {
    id: `bubble:${representative?.id ?? stableBubbleKey(args.kind, args.groupKind, args.label)}`,
    type: 'compass_bubble',
    label: args.label,
    kind: args.kind,
    referenceId: representative?.id ?? stableBubbleKey(args.kind, args.groupKind, args.label),
    memberIds,
    groupKind: args.groupKind,
    resourceCount: members.length,
    tierBreakdown,
    ...(costCarriers > 0
      ? {
          monthlyCostUsd,
          costCoverage: members.length > 0 ? costCarriers / members.length : 0,
        }
      : {}),
    ...(utilizationCarriers > 0
      ? {
          utilizationPct: utilizationPctTotal / utilizationCarriers,
          ...(wastedMonthlyUsd > 0 ? { wastedMonthlyUsd } : {}),
        }
      : {}),
    interior,
    scope: computeBubbleScope(memberIds, args.parentOf, args.byId),
  };
}

function compareBubbles(a: BubbleNode, b: BubbleNode): number {
  if (b.resourceCount !== a.resourceCount) return b.resourceCount - a.resourceCount;
  return a.label.localeCompare(b.label);
}

function stableBubbleKey(kind: string, groupKind: string, label: string): string {
  return `${kind}:${groupKind}:${label.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase()}`;
}

function awsTagsOf(node: PersistedNode): Record<string, string> {
  const props = (node.props ?? {}) as Record<string, unknown>;
  const rawTags = props.tags;
  if (!rawTags || typeof rawTags !== 'object' || Array.isArray(rawTags)) return {};
  const tags: Record<string, string> = {};
  for (const [key, value] of Object.entries(rawTags as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim()) tags[key] = value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') tags[key] = String(value);
  }
  return tags;
}

function tagValue(tags: Record<string, string>, names: readonly string[]): string | null {
  for (const name of names) {
    const direct = tags[name];
    if (direct) return direct;
  }
  const lower = new Map(Object.entries(tags).map(([key, value]) => [key.toLowerCase(), value]));
  for (const name of names) {
    const value = lower.get(name.toLowerCase());
    if (value) return value;
  }
  return null;
}

function scopedLabel(value: string, scopeLabel: string): string {
  if (!scopeLabel || scopeLabel === 'Global') return value;
  return `${value} · ${scopeLabel}`;
}

function nodeDisplayLabel(node: PersistedNode | undefined): string | null {
  if (!node) return null;
  const props = (node.props ?? {}) as Record<string, unknown>;
  const tags = awsTagsOf(node);
  const fromTag = tagValue(tags, ['Name', 'name']);
  if (fromTag) return fromTag;
  if (typeof props.name === 'string' && props.name.trim()) return props.name.trim();
  if (typeof node.label === 'string' && node.label.trim()) return node.label.trim();
  return null;
}

function resourceTypeLabel(resourceType: string): string {
  const KNOWN: Record<string, string> = {
    alb: 'ALB',
    nlb: 'NLB',
    elb: 'ELB',
    ec2: 'EC2',
    ecs: 'ECS',
    eks: 'EKS',
    rds: 'RDS',
    s3: 'S3',
    sg: 'Security Groups',
    security_group: 'Security Groups',
    iam: 'IAM',
    iam_role: 'IAM Roles',
    igw: 'Internet Gateways',
    nat: 'NAT Gateways',
    vpc_endpoint: 'VPC Endpoints',
  };
  return KNOWN[resourceType] ?? resourceType
    .split('_')
    .filter(Boolean)
    .map((part) => part.length <= 3 ? part.toUpperCase() : part[0].toUpperCase() + part.slice(1))
    .join(' ');
}

function describeFocus(focus: FocusSpec, zoom: ZoomLevel): string {
  return `${focus.kind}@L${zoom}`;
}

function resolveFocusIds(
  focus: FocusSpec,
  graph: PersistedGraph,
  byId: Map<string, PersistedNode>,
  childrenOf: Map<string, string[]>,
  findAncestor: (id: string, kind: string) => string | null,
): Set<string> {
  switch (focus.kind) {
    case 'overview':
      return new Set(graph.nodes.map((n) => n.id));

    case 'account': {
      const include = new Set<string>();
      include.add(focus.accountId);
      collectDescendants(focus.accountId, childrenOf, include);
      return include;
    }

    case 'vpc': {
      const include = new Set<string>();
      include.add(focus.vpcId);
      collectDescendants(focus.vpcId, childrenOf, include);
      if (focus.radius && focus.radius > 0) {
        expandByEdgeHops(include, graph, focus.radius);
      }
      return include;
    }

    case 'subnet': {
      const include = new Set<string>();
      include.add(focus.subnetId);
      collectDescendants(focus.subnetId, childrenOf, include);
      // Include enclosing VPC for frame context so the subnet doesn't
      // float alone.
      const vpcId = findAncestor(focus.subnetId, 'vpc');
      if (vpcId) include.add(vpcId);
      return include;
    }

    case 'resource': {
      const include = new Set<string>();
      include.add(focus.resourceId);
      const radius = focus.radius ?? 1;
      expandByEdgeHops(include, graph, radius);
      // Include enclosing containers so the resource renders inside
      // its subnet/vpc frame.
      for (const kind of ['subnet', 'vpc', 'account'] as const) {
        const a = findAncestor(focus.resourceId, kind);
        if (a) include.add(a);
      }
      return include;
    }

    case 'preset':
      return resolvePresetIds(focus.presetId, graph, byId);
  }
}

function collectDescendants(
  rootId: string,
  childrenOf: Map<string, string[]>,
  into: Set<string>,
): void {
  const queue = [...(childrenOf.get(rootId) ?? [])];
  while (queue.length) {
    const id = queue.shift()!;
    if (into.has(id)) continue;
    into.add(id);
    const kids = childrenOf.get(id);
    if (kids) queue.push(...kids);
  }
}

function expandByEdgeHops(
  ids: Set<string>,
  graph: PersistedGraph,
  radius: number,
): void {
  // BFS through non-containment edges. Containment expansion is
  // handled separately via collectDescendants so the user can say
  // `radius: 0` to scope strictly to a resource.
  let frontier = new Set(ids);
  for (let hop = 0; hop < radius; hop++) {
    const next = new Set<string>();
    for (const e of graph.edges) {
      if (e.type === 'contains' || e.type === 'containment') continue;
      if (frontier.has(e.from) && !ids.has(e.to)) next.add(e.to);
      if (frontier.has(e.to) && !ids.has(e.from)) next.add(e.from);
    }
    if (next.size === 0) break;
    for (const id of next) ids.add(id);
    frontier = next;
  }
}

/**
 * Presets replace the old per-view queries. Each returns the set of
 * node ids that should be in the slice; the rest of the pipeline
 * treats them identically to any other focus.
 */
function resolvePresetIds(
  presetId: string,
  graph: PersistedGraph,
  byId: Map<string, PersistedNode>,
): Set<string> {
  switch (presetId) {
    case 'attack-surface':
    case 'public-exposure': {
      // Every resource reachable from an internet gateway / ALB / etc.
      // Cheap proxy: include ingress-tier resources + anything they
      // point at within 2 hops.
      const seeds = new Set<string>();
      for (const n of graph.nodes) {
        const rt = resourceTypeOf(n);
        if (
          rt === 'igw' || rt === 'internet_gateway' ||
          rt === 'alb' || rt === 'nlb' || rt === 'elb' ||
          rt === 'api_gateway' || rt === 'cloudfront'
        ) {
          seeds.add(n.id);
        }
      }
      expandByEdgeHops(seeds, graph, 2);
      return seeds;
    }
    case 'public-path-to-data': {
      const ids = new Set<string>();
      const publicSeeds = new Set<string>();
      const dataStores = new Set<string>();
      for (const n of graph.nodes) {
        const rt = resourceTypeOf(n);
        if (
          rt === 'igw' || rt === 'internet_gateway' ||
          rt === 'alb' || rt === 'nlb' || rt === 'elb' ||
          rt === 'api_gateway' || rt === 'cloudfront'
        ) {
          publicSeeds.add(n.id);
        }
        if (
          rt === 'rds' || rt === 'rds_instance' || rt === 'rds_cluster' ||
          rt === 'dynamodb' || rt === 'elasticache' || rt === 'redshift' ||
          rt === 'documentdb' || rt === 'neptune' || rt === 's3' ||
          rt === 's3_bucket'
        ) {
          dataStores.add(n.id);
        }
      }
      expandByEdgeHops(publicSeeds, graph, 3);
      for (const id of publicSeeds) ids.add(id);
      for (const id of dataStores) {
        const candidate = byId.get(id);
        if (!candidate) continue;
        const exposure = publicExposureLevel(candidate);
        if (publicSeeds.has(id) || exposure === 'open' || exposure === 'restricted') {
          ids.add(id);
        }
      }
      return ids;
    }
    case 'compute-fleet': {
      const ids = new Set<string>();
      for (const n of graph.nodes) {
        const rt = resourceTypeOf(n);
        if (rt === 'ec2' || rt === 'ecs' || rt === 'ecs_service' || rt === 'lambda' ||
            rt === 'asg' || rt === 'eks' || rt === 'fargate' || rt === 'batch') {
          ids.add(n.id);
        }
      }
      return ids;
    }
    case 'data-stores': {
      const ids = new Set<string>();
      for (const n of graph.nodes) {
        const rt = resourceTypeOf(n);
        if (rt === 'rds' || rt === 'rds_instance' || rt === 'rds_cluster' ||
            rt === 'dynamodb' || rt === 'elasticache' || rt === 'redshift' ||
            rt === 'documentdb' || rt === 'neptune') {
          ids.add(n.id);
        }
      }
      return ids;
    }
    case 'iam-surface': {
      const ids = new Set<string>();
      for (const n of graph.nodes) {
        const rt = resourceTypeOf(n);
        if (rt === 'iam' || rt === 'iam_role' || rt === 'iam_policy' || rt === 'iam_user') {
          ids.add(n.id);
        }
      }
      // Include immediate neighbours so each IAM role shows what uses it.
      expandByEdgeHops(ids, graph, 1);
      return ids;
    }
    case 'identity-attack-paths': {
      const ids = new Set<string>();
      for (const n of graph.nodes) {
        const rt = resourceTypeOf(n);
        const props = (n.props ?? {}) as Record<string, unknown>;
        const text = `${n.label ?? ''} ${JSON.stringify(props)}`.toLowerCase();
        const isIam =
          rt === 'iam' || rt === 'iam_role' || rt === 'iam_policy' || rt === 'iam_user' ||
          rt === 'iam_group' || rt === 'instance_profile';
        const isIdentityFinding =
          props.angle === 'identity' ||
          props.kind === 'attack_path' ||
          props.kind === 'policy_violation' ||
          text.includes('assumerole') ||
          text.includes('administratoraccess') ||
          text.includes('wildcard') ||
          text.includes('cross-account');
        if (isIam || isIdentityFinding) ids.add(n.id);
      }
      expandByEdgeHops(ids, graph, 2);
      return ids;
    }
    case 'compliance-findings': {
      const ids = new Set<string>();
      for (const n of graph.nodes) {
        const props = (n.props ?? {}) as Record<string, unknown>;
        const complianceType = typeof props.complianceType === 'string'
          ? props.complianceType.toLowerCase()
          : '';
        const hasConfigRules = Array.isArray(props.configRuleList) && props.configRuleList.length > 0;
        const isComplianceSignal =
          props.angle === 'compliance' ||
          props.kind === 'finding' && props.source === 'aws-hosted-sync' ||
          complianceType === 'non_compliant' ||
          hasConfigRules;
        if (isComplianceSignal) ids.add(n.id);
      }
      expandByEdgeHops(ids, graph, 1);
      return ids;
    }
    case 'observability-gaps': {
      const ids = new Set<string>();
      for (const n of graph.nodes) {
        if (isContainerNode(n)) continue;
        const props = (n.props ?? {}) as Record<string, unknown>;
        const alarmCount = numberProp(props.alarmCount);
        const logGroupCount = numberProp(props.logGroupCount);
        const hasAlarm = props.hasAlarm === true || props.alarmCount === true || (alarmCount != null && alarmCount > 0);
        const hasLogs = props.hasLogGroup === true || (logGroupCount != null && logGroupCount > 0);
        const observabilityStatus =
          typeof props.observabilityStatus === 'string'
            ? props.observabilityStatus.toLowerCase()
            : '';
        if (
          props.angle === 'observability' ||
          observabilityStatus === 'missing' ||
          observabilityStatus === 'gap' ||
          props.hasAlarm === false ||
          props.hasLogGroup === false ||
          (!hasAlarm && !hasLogs && isRuntimeResource(n))
        ) {
          ids.add(n.id);
        }
      }
      return ids;
    }
    case 'ownership-gaps': {
      const ids = new Set<string>();
      for (const n of graph.nodes) {
        const props = (n.props ?? {}) as Record<string, unknown>;
        const tags = props.tags && typeof props.tags === 'object' && !Array.isArray(props.tags)
          ? props.tags as Record<string, unknown>
          : {};
        const owner =
          props.owner ?? props.ownerEmail ?? props.team ?? tags.owner ?? tags.Owner ?? tags.team ?? tags.Team;
        const costCenter = props.costCenter ?? tags.costCenter ?? tags.CostCenter ?? tags['cost-center'];
        if (!owner || !costCenter) ids.add(n.id);
      }
      return ids;
    }
    case 'cost-waste': {
      const ids = new Set<string>();
      for (const n of graph.nodes) {
        const props = (n.props ?? {}) as Record<string, unknown>;
        const monthlySpend = numberProp(props.monthlySpend ?? props.monthlyCostUsd ?? props.costMonthlyUsd);
        const utilization = numberProp(props.utilizationPct ?? props.cpuUtilizationPct ?? props.avgCpuPct);
        const waste = numberProp(props.wasteMonthlyUsd ?? props.estimatedWasteUsd);
        const ageDays = numberProp(props.ageDays);
        const rawAmount = numberProp(nestedProp(props, ['raw', 'amount']));
        const isCostFinding =
          props.angle === 'cost' ||
          props.kind === 'cost_anomaly' ||
          (typeof props.scannerId === 'string' && props.scannerId.includes('cost'));
        if (
          isCostFinding ||
          (waste != null && waste > 0) ||
          (monthlySpend != null && monthlySpend >= 50 && utilization != null && utilization <= 20) ||
          (monthlySpend != null && monthlySpend >= 50 && ageDays != null && ageDays >= 90) ||
          (rawAmount != null && rawAmount > 0)
        ) {
          ids.add(n.id);
        }
      }
      // Pull in the resources each finding affects so the canvas shows
      // the impacted infrastructure next to the cost evidence, not just
      // floating finding nodes.
      const expanded = new Set(ids);
      for (const id of ids) {
        const n = byId.get(id);
        if (!n) continue;
        const props = (n.props ?? {}) as Record<string, unknown>;
        const affected = props.affectedResourceKeys;
        if (Array.isArray(affected)) {
          for (const key of affected) {
            if (typeof key === 'string' && byId.has(key)) expanded.add(key);
          }
        }
      }
      return expanded;
    }
    case 'drift-from-baseline': {
      const ids = new Set<string>();
      for (const n of graph.nodes) {
        const props = (n.props ?? {}) as Record<string, unknown>;
        const drift =
          props.driftStatus ?? props.baselineDelta ?? props.changeStatus ?? props.scenarioStatus;
        if (typeof drift === 'string' && drift.trim() && drift !== 'unchanged') {
          ids.add(n.id);
        }
      }
      return ids;
    }
    case 'planned-changes': {
      const ids = new Set<string>();
      const plannedStatuses = new Set([
        'planned',
        'proposed',
        'pending',
        'added',
        'changed',
        'modified',
        'removed',
        'deleted',
      ]);
      for (const n of graph.nodes) {
        const props = (n.props ?? {}) as Record<string, unknown>;
        const scenarioStatus = props.scenarioStatus ?? props.planStatus ?? props.changeStatus;
        const changeSource = props.changeSource ?? props.planSourceKind ?? props.iacPlanSourceKind;
        const hasPlanId = !!(props.scenarioId ?? props.changeSetId ?? props.planId ?? props.iacPlanId);
        if (
          hasPlanId ||
          (typeof scenarioStatus === 'string' && plannedStatuses.has(scenarioStatus.trim().toLowerCase())) ||
          (typeof changeSource === 'string' && /terraform|cloudformation|cfn|iac/i.test(changeSource))
        ) {
          ids.add(n.id);
        }
      }
      return ids;
    }
    default:
      // Unknown preset → empty slice. Caller handles empty state.
      void byId;
      return new Set();
  }
}

/**
 * At zoom levels 1–3 we collapse leaf resources under each structural
 * container into a synthetic bubble with counts. The bubbles become
 * the render primitives at those zoom levels; clicking one drops into
 * the container as the new focus.
 *
 * L1: bubbles for accounts (or regions if no accounts present).
 * L2: bubbles for VPCs.
 * L3: bubbles for subnets.
 */
function collapseForOverview(
  zoom: ZoomLevel,
  containers: PersistedNode[],
  leaves: PersistedNode[],
  parentOf: Map<string, string>,
  childrenOf: Map<string, string[]>,
  byId: Map<string, PersistedNode>,
): { bubbles: BubbleNode[]; survivingContainers: PersistedNode[] } {
  const bubbleKind: BubbleNode['kind'] = zoom === 1 ? 'account' : zoom === 3 ? 'subnet' : 'vpc';
  const bubbleContainers = containers.filter((c) => resourceTypeOf(c) === bubbleKind);

  // Fallback: at L1 if there are no accounts, use regions. At L2 if
  // there are no VPCs, use accounts. L3 intentionally does not fall
  // back to VPCs, because that would make L2 and L3 visually identical.
  const effectiveBubbleContainers = bubbleContainers.length > 0
    ? bubbleContainers
    : zoom === 1
      ? containers.filter((c) => resourceTypeOf(c) === 'region')
      : zoom === 2
        ? containers.filter((c) => resourceTypeOf(c) === 'account')
        : [];

  if (effectiveBubbleContainers.length === 0) {
    // Nothing to collapse — pass containers through so the user at
    // least sees structure.
    return { bubbles: [], survivingContainers: containers };
  }

  const bubbles: BubbleNode[] = [];
  const coveredLeafIds = new Set<string>();
  for (const container of effectiveBubbleContainers) {
    const descendants = new Set<string>();
    collectDescendants(container.id, childrenOf, descendants);
    let resourceCount = 0;
    let costCarriers = 0;
    let monthlyCostUsd = 0;
    let utilizationCarriers = 0;
    let utilizationPctTotal = 0;
    let wastedMonthlyUsd = 0;
    const tierBreakdown: Record<string, number> = {};
    // Collect descendant leaf ids so the scene builder's cost-
    // attribution roll-up can find them by id later. Without this
    // list, VPC/account bubbles at L1–L3 show "no cost" even when
    // every leaf inside them has attributed spend, because the
    // roll-up walks `bubble.memberIds`.
    const memberIds: string[] = [];
    for (const id of descendants) {
      const n = byId.get(id);
      if (!n) continue;
      if (isContainerNode(n)) continue;
      coveredLeafIds.add(n.id);
      memberIds.push(n.id);
      resourceCount++;
      const rt = resourceTypeOf(n);
      tierBreakdown[rt] = (tierBreakdown[rt] ?? 0) + 1;
      const monthly = readMonthlyCostUsd(n);
      if (monthly !== null) {
        costCarriers++;
        monthlyCostUsd += monthly;
      }
      const utilization = readUtilizationPct(n);
      if (utilization !== null) {
        utilizationCarriers++;
        utilizationPctTotal += utilization;
        if (monthly !== null) {
          wastedMonthlyUsd += monthly * (1 - Math.max(0, Math.min(100, utilization)) / 100);
        }
      }
    }
    // Scope for structural bubbles answers "where does this VPC /
    // subnet / account sit?" — derived from the container's own
    // ancestors, not its members. A VPC bubble shows account/region;
    // a subnet bubble shows account/region/vpc.
    const bubbleScope = computeBubbleScope([container.id], parentOf, byId);
    bubbles.push({
      id: `bubble:${container.id}`,
      type: 'compass_bubble',
      label: String(container.label ?? container.id),
      kind: resourceTypeOf(container) as BubbleNode['kind'],
      referenceId: container.id,
      memberIds,
      resourceCount,
      tierBreakdown,
      ...(bubbleScope ? { scope: bubbleScope } : {}),
      ...(costCarriers > 0
        ? {
            monthlyCostUsd,
            costCoverage: resourceCount > 0 ? costCarriers / resourceCount : 0,
          }
        : {}),
      ...(utilizationCarriers > 0
        ? {
            utilizationPct: utilizationPctTotal / utilizationCarriers,
            ...(wastedMonthlyUsd > 0 ? { wastedMonthlyUsd } : {}),
          }
        : {}),
      // Always populate the interior list. The renderer decides
      // whether to show it compact (tiny dots) or expanded (readable
      // grid) based on the `expanded` attr — but either way the
      // density of a bubble should be visible at a glance, not hidden
      // behind an explicit expand gesture.
      interior: Array.from(descendants).reduce<BubbleInteriorDot[]>((acc, id) => {
        const n = byId.get(id);
        if (!n || isContainerNode(n)) return acc;
        acc.push({
          id: n.id,
          resourceType: resourceTypeOf(n),
          label: String(n.label ?? n.id),
        });
        return acc;
      }, []),
    });
  }

  if (zoom === 3) {
    const looseLeaves = leaves.filter((leaf) => !coveredLeafIds.has(leaf.id));
    const looseResourceBubbles = collapseForResourceTypes(looseLeaves, byId, parentOf);
    bubbles.push(...looseResourceBubbles);
  }

  return { bubbles, survivingContainers: [] };
}

/**
 * Read monthly cost in USD off a graph node's props. Accepts two
 * shapes so both the first-class `ImportedResourceCost` path and the
 * flat `props.cost: number` path used by the cost-lens heatmap work.
 * Non-USD currencies return `null` — we don't FX-convert; users
 * handling mixed currencies should pre-normalise on ingest.
 */
function readMonthlyCostUsd(node: PersistedNode): number | null {
  const props = (node.props ?? {}) as Record<string, unknown>;
  const raw = props.cost;
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  if (typeof raw === 'object') {
    const rec = raw as { monthly?: unknown; currency?: unknown };
    const monthly = rec.monthly;
    const currency = typeof rec.currency === 'string' ? rec.currency.toUpperCase() : 'USD';
    if (currency !== 'USD') return null;
    return typeof monthly === 'number' && Number.isFinite(monthly) ? monthly : null;
  }
  return null;
}

function readUtilizationPct(node: PersistedNode): number | null {
  const props = (node.props ?? {}) as Record<string, unknown>;
  const direct = firstNumeric(
    props.utilizationPct,
    props.utilization,
    props.cpuUtilizationPct,
    props.cpuUtilization,
    props.averageCpuUtilization,
    props.memoryUtilizationPct,
  );
  if (direct !== null) return normalisePct(direct);
  const state = String(props.state ?? '').toLowerCase();
  if (state === 'stopped' || state === 'terminated' || state === 'idle') return 0;
  return null;
}

function firstNumeric(...values: unknown[]): number | null {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const parsed = Number(value.replace(/[%,$]/g, '').trim());
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function normalisePct(value: number): number {
  return value > 0 && value <= 1 ? value * 100 : value;
}
